import React from 'react';

/*
 * Hand-drawn SVG icon set.
 *
 * Every icon is plain stroke art on a 24x24 grid using `currentColor`, run
 * through a shared feTurbulence/feDisplacement "roughen" filter so the lines
 * pick up a wobbly, pencil-sketched character that matches the Sketchbook
 * theme. No emoji, no icon fonts — the geometry is drawn here by hand.
 *
 * The filter id is intentionally identical across instances: the definition is
 * the same everywhere, so a shared id resolves correctly no matter how many
 * icons are on the page.
 */

const Roughen = ({ id, amount = 1.4 }) => (
  <filter id={id} x="-20%" y="-20%" width="140%" height="140%">
    <feTurbulence type="fractalNoise" baseFrequency="0.018" numOctaves="2" seed="7" result="noise" />
    <feDisplacementMap in="SourceGraphic" in2="noise" scale={amount} />
  </filter>
);

const Svg = ({ size = 24, roughen = true, strokeWidth = 1.9, children, className, style, title }) => (
  <svg
    className={className}
    style={style}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    role={title ? 'img' : 'presentation'}
    aria-hidden={title ? undefined : true}
    aria-label={title}
    focusable="false"
  >
    {roughen && (
      <defs>
        <Roughen id="sketch-roughen" />
      </defs>
    )}
    <g filter={roughen ? 'url(#sketch-roughen)' : undefined}>{children}</g>
  </svg>
);

/* ---- Large concept icons (Initiatives cards) ---- */

export const GraduationCapIcon = (props) => (
  <Svg {...props}>
    <path d="M2.5 8.5 12 4.5l9.5 4-9.5 4-9.5-4Z" />
    <path d="M6.5 11v4.2c0 1.2 2.7 2.3 5.5 2.3s5.5-1.1 5.5-2.3V11" />
    <path d="M21 8.6v4.6" />
    <path d="M21 13.2c-.5.9-.5 1.8 0 2.6" />
  </Svg>
);

export const GamepadIcon = (props) => (
  <Svg {...props}>
    <path d="M7.5 8.5h9c2.2 0 3.7 1.8 4 4l.5 3.4c.2 1.6-.9 2.9-2.3 2.6-1-.2-1.7-1-2.4-1.8l-.8-.9H8.2l-.8.9c-.7.8-1.4 1.6-2.4 1.8-1.4.3-2.5-1-2.3-2.6L3 12.5c.3-2.2 1.8-4 4.5-4Z" />
    <path d="M6.2 12.4v2.4M5 13.6h2.4" />
    <circle cx="15.5" cy="12.6" r="0.6" />
    <circle cx="17.6" cy="14.4" r="0.6" />
  </Svg>
);

export const ChartIcon = (props) => (
  <Svg {...props}>
    <path d="M4 4v15.5h16" />
    <path d="M8 16v-3.5" />
    <path d="M12 16V8.5" />
    <path d="M16 16v-6" />
    <path d="M7 8.5 11 6l3 2 3-3.2" />
  </Svg>
);

export const HandshakeIcon = (props) => (
  <Svg {...props}>
    <circle cx="7.5" cy="7" r="2.4" />
    <circle cx="16.5" cy="7" r="2.4" />
    <path d="M3 19v-2.4C3 14 4.8 12.4 7.5 12.4c1.4 0 2.5.4 3.3 1.2" />
    <path d="M21 19v-2.4c0-2.6-1.8-4.2-4.5-4.2-1.4 0-2.5.4-3.3 1.2" />
    <path d="M9.6 16.2 12 18.4l2.4-2.2" />
  </Svg>
);

export const GlobeIcon = (props) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="8.2" />
    <path d="M3.8 12h16.4" />
    <path d="M12 3.8c2.6 2.3 4 5.1 4 8.2s-1.4 5.9-4 8.2c-2.6-2.3-4-5.1-4-8.2s1.4-5.9 4-8.2Z" />
  </Svg>
);

export const FlaskIcon = (props) => (
  <Svg {...props}>
    <path d="M9.5 3.5h5" />
    <path d="M10 3.8v5.4L5.4 17c-.8 1.4.1 3.2 1.8 3.2h9.6c1.7 0 2.6-1.8 1.8-3.2L14 9.2V3.8" />
    <path d="M7.7 14.2h8.6" />
    <circle cx="10.5" cy="16.6" r="0.5" />
    <circle cx="13.6" cy="17.6" r="0.5" />
  </Svg>
);

/* ---- Small inline icons (lists, notices) ---- */

export const CheckIcon = (props) => (
  <Svg strokeWidth={2.4} {...props}>
    <path d="M4.5 12.5 9.5 18 20 6" />
  </Svg>
);

export const CrossIcon = (props) => (
  <Svg strokeWidth={2.4} {...props}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Svg>
);

export const LockIcon = (props) => (
  <Svg {...props}>
    <rect x="5" y="10.5" width="14" height="9.5" rx="2.2" />
    <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" />
    <path d="M12 14v2.5" />
  </Svg>
);

export const ShieldIcon = (props) => (
  <Svg {...props}>
    <path d="M12 3.5 19 6v5.5c0 4.3-2.9 7.4-7 9-4.1-1.6-7-4.7-7-9V6l7-2.5Z" />
    <path d="M8.8 11.8 11 14l4.2-4.4" />
  </Svg>
);

export const WarnIcon = (props) => (
  <Svg {...props}>
    <path d="M12 4.2 21 19.5H3L12 4.2Z" />
    <path d="M12 10v4" />
    <circle cx="12" cy="16.6" r="0.5" />
  </Svg>
);

export const GearIcon = (props) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="3.2" />
    <path d="M12 3.5v2.2M12 18.3v2.2M3.5 12h2.2M18.3 12h2.2M6 6l1.6 1.6M16.4 16.4 18 18M18 6l-1.6 1.6M7.6 16.4 6 18" />
  </Svg>
);

export const PauseIcon = (props) => (
  <Svg {...props}>
    <path d="M9 5.5v13M15 5.5v13" />
  </Svg>
);

export const ArrowLeftIcon = (props) => (
  <Svg strokeWidth={2.1} {...props}>
    <path d="M19 12H5" />
    <path d="M11 6 5 12l6 6" />
  </Svg>
);

export const SparkIcon = (props) => (
  <Svg strokeWidth={1.7} {...props}>
    <path d="M12 4.5c.6 3.4 2.6 5.4 6 6-3.4.6-5.4 2.6-6 6-.6-3.4-2.6-5.4-6-6 3.4-.6 5.4-2.6 6-6Z" />
  </Svg>
);

export const StarIcon = (props) => (
  <Svg strokeWidth={1.7} {...props}>
    <path d="M12 4.5l2.2 4.6 5 .6-3.7 3.4 1 4.9L12 16l-4.5 2.4 1-4.9L4.8 9.7l5-.6L12 4.5Z" />
  </Svg>
);

export default {
  GraduationCapIcon,
  GamepadIcon,
  ChartIcon,
  HandshakeIcon,
  GlobeIcon,
  FlaskIcon,
  CheckIcon,
  CrossIcon,
  LockIcon,
  ShieldIcon,
  WarnIcon,
  GearIcon,
  PauseIcon,
  ArrowLeftIcon,
  SparkIcon,
  StarIcon,
};
